import React, { useState } from "react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ResponsiveContainer, XAxis, YAxis, Tooltip, Bar, BarChart, CartesianGrid } from "recharts";
import { Zap, TrendingDown, Leaf, Euro, Sun, Home as HomeIcon, Calculator as CalcIcon, Factory, Map as MapIcon, MapPin, Ruler, Compass, Cloud, Battery, Cpu, AlertTriangle, CheckCircle2, FileDown, CalendarCheck, X, Loader2, Search } from "lucide-react";

const DEFAULT_CITIES_IT = ["Roma", "Milano", "Napoli", "Torino", "Firenze", "Bologna", "Palermo", "Bari", "Venezia", "Genova"];
const CITIES_PL = ["Warszawa", "Kraków", "Łódź", "Wrocław", "Poznań", "Gdańsk", "Szczecin", "Katowice", "Lublin", "Białystok", "Rzeszów"];

const PRESETS = {
  residential: { monthly_kwh: 300, roof_area_m2: 50, system_cost_per_kw: 1800, electricity_price: 0.28 },
  industrial: { monthly_kwh: 12000, roof_area_m2: 1500, system_cost_per_kw: 1100, electricity_price: 0.22 },
  ground: { monthly_kwh: 50000, roof_area_m2: 10000, system_cost_per_kw: 950, electricity_price: 0.20 },
};

const TERRAIN_OPTIONS = {
  residential: [
    { v: "pitched_roof", l: "Tetto a falde" },
    { v: "flat_roof", l: "Tetto piano" },
    { v: "balcony", l: "Balcone" },
  ],
  industrial: [
    { v: "warehouse", l: "Capannone" },
    { v: "flat_roof", l: "Tetto piano industriale" },
    { v: "pitched_roof", l: "Tetto a falde industriale" },
  ],
  ground: [
    { v: "ground_agri", l: "Terreno agricolo" },
    { v: "ground_free", l: "Terreno libero" },
  ],
};

export default function Calculator() {
  const { t, user } = useApp();
  const [cities, setCities] = React.useState(DEFAULT_CITIES_IT);
  const [systemType, setSystemType] = useState("residential");
  const [form, setForm] = useState({
    ...PRESETS.residential,
    location: "Roma",
    orientation: "south",
    system_type: "residential",
    country: "italy",
    address: "",
    terrain_type: "pitched_roof",
    length_m: 10,
    width_m: 5,
    tilt_degrees: 30,
    shading: "none",
    panel_wattage: 450,
    battery_desired: false,
    lat: null,
    lon: null,
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [label, setLabel] = useState("");
  const [visitOpen, setVisitOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  React.useEffect(() => {
    api.get("/cities").then(({ data }) => Array.isArray(data) && setCities(data)).catch(() => {});
  }, []);

  const switchType = (type) => {
    setSystemType(type);
    setForm((f) => ({
      ...f,
      ...PRESETS[type],
      system_type: type,
      terrain_type: TERRAIN_OPTIONS[type][0].v,
      panel_wattage: type === "residential" ? 450 : 550,
    }));
    setResult(null);
  };

  const update = (k, v) => setForm((f) => {
    const nf = { ...f, [k]: v };
    // Auto-calculate area when length × width changes
    if (k === "length_m" || k === "width_m") {
      const l = k === "length_m" ? v : nf.length_m;
      const w = k === "width_m" ? v : nf.width_m;
      if (l > 0 && w > 0) nf.roof_area_m2 = Math.round(l * w);
    }
    // Switch cities based on country
    if (k === "country") {
      const newCities = v === "poland" ? CITIES_PL : DEFAULT_CITIES_IT;
      setCities(newCities);
      nf.location = newCities[0];
      nf.lat = null;
      nf.lon = null;
    }
    // Manually editing city or address invalidates any previous geocoded coords
    if (k === "location" || k === "address") {
      nf.lat = null;
      nf.lon = null;
    }
    return nf;
  });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/calculator/estimate", { ...form });
      setResult(data);
      window.scrollTo({ top: document.getElementById("results")?.offsetTop || 0, behavior: "smooth" });
    } catch {
      toast.error("Errore calcolo");
    } finally { setLoading(false); }
  };

  const save = async () => {
    if (!user) { toast.error("Accedi per salvare"); return; }
    try {
      const { data } = await api.post("/calculator/estimate", { ...form, save: true, label: label || "Preventivo" });
      setResult(data);
      toast.success("Preventivo salvato");
    } catch (e) {
      if (e?.response?.status === 402) toast.error("Riservato al piano Pro");
      else toast.error("Errore");
    }
  };

  const downloadPdf = async () => {
    if (!result) return;
    setPdfLoading(true);
    try {
      const res = await api.post("/calculator/pdf", {
        calc_input: form,
        calc_result: result,
        client_name: user?.name || "",
        client_email: user?.email || "",
      }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `preventivo_${form.location.toLowerCase()}_${new Date().toISOString().slice(0,10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF preventivo scaricato");
    } catch {
      toast.error("Errore generazione PDF");
    } finally {
      setPdfLoading(false);
    }
  };

  const chartData = result?.monthly_production?.map((v, i) => ({
    month: ["Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"][i], kwh: v,
  })) || [];

  const terrainList = TERRAIN_OPTIONS[systemType] || [];

  return (
    <div className="min-h-screen bg-[#F9F8F6] grain-bg pb-16">
      <div className="max-w-7xl mx-auto px-6 pt-12">
        <div className="mb-10">
          <div className="label-eco mb-4"><CalcIcon size={12} className="inline mr-1" /> Preventivo · Fotovoltaico</div>
          <h1 className="text-5xl font-black tracking-tight text-[#17301C]">Calcolatore preventivo dettagliato</h1>
          <p className="text-lg text-[#3A4A3E] mt-3 max-w-2xl">
            Inserisci posizione esatta, misure del terreno/tetto, marca inverter e batteria — calcoliamo esattamente quanti pannelli servono.
          </p>

          <div className="mt-8 inline-flex p-1 rounded-full bg-white border border-[#DDE0D8] flex-wrap" data-testid="system-type-toggle">
            {[["residential", HomeIcon, t.calculator.residential], ["industrial", Factory, t.calculator.industrial], ["ground", MapIcon, t.calculator.ground]].map(([type, Icon, label]) => (
              <button key={type} type="button" onClick={() => switchType(type)} data-testid={`system-type-${type}`}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors ${systemType === type ? "bg-[#17301C] text-white" : "text-[#17301C] hover:bg-[#EAE7E0]"}`}>
                <Icon size={15} /> {label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <form onSubmit={submit} className="lg:col-span-5 card-surface p-6 space-y-5" data-testid="calc-form">

            {/* Sezione: LOCATION */}
            <Section title="1. Posizione" icon={MapPin}>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Paese">
                  <select data-testid="calc-country" value={form.country} onChange={(e) => update("country", e.target.value)} className="inp">
                    <option value="italy">🇮🇹 Italia</option>
                    <option value="poland">🇵🇱 Polonia</option>
                    <option value="other">🌍 Altro</option>
                  </select>
                </FormField>
                <FormField label="Città / Comune">
                  <input
                    data-testid="calc-location"
                    list="calc-cities-datalist"
                    value={form.location}
                    onChange={(e) => update("location", e.target.value)}
                    placeholder="Es. Rocca di Neto, Strongoli, Roma..."
                    className="inp"
                    autoComplete="off"
                  />
                  <datalist id="calc-cities-datalist">
                    {cities.map((c) => <option key={c} value={c} />)}
                  </datalist>
                </FormField>
              </div>
              <FormField label="Indirizzo completo (opzionale ma consigliato)">
                <AddressAutocomplete
                  value={form.address}
                  country={form.country}
                  onChange={(v) => update("address", v)}
                  onSelect={(sel) => {
                    setForm((f) => ({
                      ...f,
                      address: sel.display_name,
                      location: sel.city || f.location,
                      lat: sel.lat ? parseFloat(sel.lat) : f.lat,
                      lon: sel.lon ? parseFloat(sel.lon) : f.lon,
                    }));
                  }}
                />
              </FormField>
            </Section>

            {/* Sezione: TERRENO/TETTO */}
            <Section title="2. Terreno / Tetto" icon={Ruler}>
              <FormField label="Tipo di superficie">
                <div className="grid grid-cols-3 gap-2">
                  {terrainList.map((tt) => (
                    <button type="button" key={tt.v} data-testid={`calc-terrain-${tt.v}`}
                      onClick={() => update("terrain_type", tt.v)}
                      className={`px-2 py-2 rounded-lg border text-xs font-semibold transition-colors ${form.terrain_type === tt.v ? "bg-[#17301C] text-white border-[#17301C]" : "bg-white border-[#DDE0D8] text-[#17301C] hover:bg-[#EAE7E0]"}`}>
                      {tt.l}
                    </button>
                  ))}
                </div>
              </FormField>
              <div className="grid grid-cols-3 gap-3">
                <FormField label="Lunghezza (m)">
                  <input data-testid="calc-length" type="number" min="1" step="0.5" value={form.length_m}
                    onChange={(e) => update("length_m", parseFloat(e.target.value))} className="inp" />
                </FormField>
                <FormField label="Larghezza (m)">
                  <input data-testid="calc-width" type="number" min="1" step="0.5" value={form.width_m}
                    onChange={(e) => update("width_m", parseFloat(e.target.value))} className="inp" />
                </FormField>
                <FormField label="Area totale (m²)">
                  <input data-testid="calc-roof-area" type="number" min="1" value={form.roof_area_m2}
                    onChange={(e) => update("roof_area_m2", parseFloat(e.target.value))} className="inp" />
                </FormField>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <FormField label="Orientamento" icon={Compass}>
                  <select data-testid="calc-orientation" value={form.orientation} onChange={(e) => update("orientation", e.target.value)} className="inp">
                    <option value="south">Sud</option>
                    <option value="south_east">Sud-Est</option>
                    <option value="south_west">Sud-Ovest</option>
                    <option value="east">Est</option>
                    <option value="west">Ovest</option>
                    <option value="north">Nord</option>
                  </select>
                </FormField>
                <FormField label="Inclinazione (°)">
                  <input data-testid="calc-tilt" type="number" min="0" max="60" value={form.tilt_degrees}
                    onChange={(e) => update("tilt_degrees", parseInt(e.target.value) || 30)} className="inp" />
                </FormField>
                <FormField label="Ombreggiamento" icon={Cloud}>
                  <select data-testid="calc-shading" value={form.shading} onChange={(e) => update("shading", e.target.value)} className="inp">
                    <option value="none">Nessuno</option>
                    <option value="partial">Parziale</option>
                    <option value="heavy">Significativo</option>
                  </select>
                </FormField>
              </div>
            </Section>

            {/* Sezione: PANNELLI E CONSUMI */}
            <Section title="3. Consumi e pannelli" icon={Zap}>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Consumo mensile (kWh)">
                  <input data-testid="calc-monthly-kwh" type="number" min="1" required value={form.monthly_kwh}
                    onChange={(e) => update("monthly_kwh", parseFloat(e.target.value))} className="inp" />
                </FormField>
                <FormField label="Potenza pannelli (W)">
                  <select data-testid="calc-panel-wattage" value={form.panel_wattage} onChange={(e) => update("panel_wattage", parseInt(e.target.value))} className="inp">
                    <option value={300}>300W (vecchia gen)</option>
                    <option value={400}>400W</option>
                    <option value={450}>450W (standard)</option>
                    <option value={500}>500W</option>
                    <option value={550}>550W (industriale)</option>
                    <option value={600}>600W (top gamma)</option>
                  </select>
                </FormField>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Prezzo energia €/kWh">
                  <input data-testid="calc-price" type="number" step="0.01" min="0.01" value={form.electricity_price}
                    onChange={(e) => update("electricity_price", parseFloat(e.target.value))} className="inp" />
                </FormField>
                <FormField label="Costo sistema €/kWp">
                  <input data-testid="calc-cost-per-kw" type="number" step="50" min="500" value={form.system_cost_per_kw}
                    onChange={(e) => update("system_cost_per_kw", parseFloat(e.target.value))} className="inp" />
                </FormField>
              </div>
              <label className="flex items-center gap-3 p-3 rounded-lg bg-[#EAB308]/10 border border-[#EAB308]/40 cursor-pointer">
                <input type="checkbox" data-testid="calc-battery-desired" checked={form.battery_desired}
                  onChange={(e) => update("battery_desired", e.target.checked)} className="w-4 h-4" />
                <span className="text-sm font-semibold text-[#17301C] flex items-center gap-2">
                  <Battery size={14} /> Voglio anche una batteria di accumulo
                </span>
              </label>
            </Section>

            <button data-testid="calc-submit" type="submit" disabled={loading}
              className="btn-primary w-full disabled:opacity-60">
              {loading ? "Calcolo in corso..." : "Calcola preventivo"}
            </button>
          </form>

          <div className="lg:col-span-7">
            {!result ? (
              <div className="card-surface p-12 h-full flex flex-col items-center justify-center text-center">
                <div className="w-16 h-16 rounded-full bg-[#EAB308] flex items-center justify-center mb-4">
                  <CalcIcon size={28} className="text-[#17301C]" />
                </div>
                <p className="text-[#6E7A71]">Compila i 3 gruppi di dati per vedere il preventivo dettagliato.</p>
              </div>
            ) : (
              <motion.div id="results" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6" data-testid="calc-results">
                <div className="grid grid-cols-2 gap-4">
                  <KPI icon={Zap} label="Potenza consigliata" value={result.recommended_kwp} unit="kWp" accent="#EAB308" />
                  <KPI icon={HomeIcon} label="Pannelli necessari" value={result.panels_estimated} unit={`× ${result.panel_wattage_used}W`} />
                  <KPI icon={Sun} label="Produzione annuale" value={result.annual_production_kwh.toLocaleString("it-IT")} unit="kWh/anno" />
                  <KPI icon={Euro} label="Risparmio annuo" value={`€${result.annual_savings_eur.toLocaleString("it-IT")}`} accent="#8BA892" />
                </div>

                <div className="card-surface p-5" data-testid="inverter-recommendation">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-9 h-9 rounded-lg bg-[#17301C] flex items-center justify-center">
                      <Cpu size={18} className="text-[#EAB308]" />
                    </div>
                    <div>
                      <div className="label-eco text-[#6E7A71]">Inverter consigliato</div>
                      <div className="text-2xl font-black font-mono-nums text-[#17301C]">{result.inverter_recommended_kw} kW</div>
                    </div>
                  </div>
                  <p className="text-sm text-[#3A4A3E]">{result.inverter_notes}</p>
                </div>

                {result.battery_recommended_kwh > 0 && (
                  <div className="card-surface p-5 border-2 border-[#EAB308]/40" data-testid="battery-recommendation">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-9 h-9 rounded-lg bg-[#EAB308] flex items-center justify-center">
                        <Battery size={18} className="text-[#17301C]" />
                      </div>
                      <div>
                        <div className="label-eco text-[#6E7A71]">Batteria consigliata</div>
                        <div className="text-2xl font-black font-mono-nums text-[#17301C]">{result.battery_recommended_kwh} kWh</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-[#6E7A71] font-bold">Costo aggiuntivo</div>
                        <div className="font-mono-nums font-black text-[#17301C]">€ {result.battery_extra_cost_eur.toLocaleString("it-IT")}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-[#6E7A71] font-bold">Autoconsumo con batteria</div>
                        <div className="font-mono-nums font-black text-[#166534]">{result.self_consumption_with_battery_pct}%</div>
                      </div>
                    </div>
                    <div className="mt-3 text-xs text-[#3A4A3E] p-2 rounded-lg bg-[#8BA892]/10">
                      💡 Con batteria: risparmio annuale aumenta a <b>€{result.annual_savings_with_battery_eur.toLocaleString("it-IT")}</b> (+€{(result.annual_savings_with_battery_eur - result.annual_savings_eur).toFixed(0)}/anno)
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-3 gap-4">
                  <KPI icon={TrendingDown} label="Rientro" value={result.payback_years} unit="anni" />
                  <KPI icon={Leaf} label="CO₂ evitata" value={result.co2_saved_tonnes_year} unit="t/anno" accent="#17301C" />
                  <KPI icon={Euro} label="Investimento" value={`€${(result.total_system_cost_eur + (result.battery_extra_cost_eur || 0)).toLocaleString("it-IT")}`} unit="totale" />
                </div>

                {result.warnings?.length > 0 && (
                  <div className="card-surface p-4 border-l-4 border-[#EAB308]" data-testid="warnings">
                    <div className="flex items-center gap-2 mb-2 text-[#8B7000] font-black text-sm">
                      <AlertTriangle size={16} /> Note tecniche
                    </div>
                    <ul className="space-y-1.5">
                      {result.warnings.map((w, i) => (
                        <li key={i} className="text-sm text-[#3A4A3E] flex items-start gap-2">
                          <span className="text-[#EAB308] mt-0.5">•</span>{w}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="card-surface p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="label-eco">Produzione mensile stimata</div>
                    <div className="text-sm text-[#6E7A71]">Totale: <span className="font-mono-nums font-bold text-[#17301C]">{result.annual_production_kwh.toLocaleString("it-IT")} kWh</span></div>
                  </div>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#DDE0D8" />
                      <XAxis dataKey="month" stroke="#6E7A71" fontSize={12} />
                      <YAxis stroke="#6E7A71" fontSize={12} />
                      <Tooltip contentStyle={{ background: "#fff", border: "1px solid #DDE0D8", borderRadius: 8 }} />
                      <Bar dataKey="kwh" fill="#EAB308" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="card-surface p-5 flex flex-wrap items-center gap-3 justify-between">
                  <div className="flex items-center gap-2 text-sm text-[#166534] font-semibold">
                    <CheckCircle2 size={16} /> Preventivo pronto per la richiesta
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={downloadPdf}
                      disabled={pdfLoading}
                      data-testid="calc-download-pdf"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 border-[#17301C] text-[#17301C] font-bold text-sm hover:bg-[#17301C] hover:text-white transition-colors disabled:opacity-50"
                    >
                      <FileDown size={14} /> {pdfLoading ? "..." : "PDF"}
                    </button>
                    <button
                      onClick={() => setVisitOpen(true)}
                      data-testid="calc-book-visit"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#EAB308] text-[#17301C] font-black text-sm hover:bg-[#f5bc1b] transition-colors"
                    >
                      <CalendarCheck size={14} /> Prenota sopralluogo
                    </button>
                    {user && (
                      <button data-testid="calc-save" onClick={save} className="btn-dash">Salva</button>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>
      <SiteVisitDialog open={visitOpen} setOpen={setVisitOpen} form={form} result={result} user={user} />
      <style>{`.inp { width: 100%; padding: 0.55rem 0.65rem; border-radius: 0.5rem; border: 1px solid #DDE0D8; background: white; font-size: 0.9rem; transition: border-color .2s; }
      .inp:focus { outline: none; border-color: #17301C; box-shadow: 0 0 0 1px #17301C; }`}</style>
    </div>
  );
}

const Section = ({ title, icon: Icon, children }) => (
  <div className="border-t border-[#DDE0D8] pt-4 first:border-t-0 first:pt-0 space-y-3">
    <div className="flex items-center gap-2 text-[#17301C] font-black text-sm uppercase tracking-widest">
      <Icon size={14} /> {title}
    </div>
    {children}
  </div>
);

const FormField = ({ label, icon: Icon, children }) => (
  <div>
    <div className="text-[10px] font-black tracking-widest uppercase text-[#6E7A71] mb-1.5 flex items-center gap-1">
      {Icon && <Icon size={11} />} {label}
    </div>
    {children}
  </div>
);

const KPI = ({ icon: Icon, label, value, unit, accent }) => (
  <div className="card-surface p-4">
    <div className="flex items-center gap-2 mb-2">
      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: accent || "#EAE7E0" }}>
        <Icon size={16} className={accent === "#17301C" ? "text-[#EAB308]" : "text-[#17301C]"} />
      </div>
      <div className="text-[10px] font-black tracking-widest uppercase text-[#6E7A71]">{label}</div>
    </div>
    <div className="text-2xl font-black font-mono-nums text-[#17301C] leading-tight">
      {value}<span className="text-xs ml-1 font-medium text-[#6E7A71]">{unit}</span>
    </div>
  </div>
);

// ============ ADDRESS AUTOCOMPLETE (via backend /api/geocode → Nominatim) ============
function AddressAutocomplete({ value, country, onChange, onSelect }) {
  const [suggestions, setSuggestions] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [fallbackHint, setFallbackHint] = React.useState("");
  const debounceRef = React.useRef(null);
  const boxRef = React.useRef(null);

  React.useEffect(() => {
    const onDocClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const handleChange = (v) => {
    onChange(v);
    setError("");
    setFallbackHint("");
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!v || v.length < 3) { setSuggestions([]); setOpen(false); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/geocode", { params: { q: v, country, limit: 6 } });
        const results = Array.isArray(data?.results) ? data.results : [];
        setSuggestions(results);
        setOpen(true);
        if (data?.used_query) {
          setFallbackHint(`Nessun risultato per "${v}". Ecco cosa ho trovato per "${data.used_query}":`);
        }
        if (results.length === 0) setError("Nessun risultato — puoi comunque digitare l'indirizzo manualmente");
      } catch {
        setSuggestions([]);
        setOpen(true);
        setError("Autocomplete non disponibile — digita l'indirizzo manualmente");
      } finally { setLoading(false); }
    }, 400);
  };

  const pick = (s) => {
    onSelect({ display_name: s.display_name, city: s.city, lat: s.lat, lon: s.lon });
    setOpen(false);
    setFallbackHint("");
  };

  return (
    <div className="relative" ref={boxRef}>
      <div className="relative">
        <input
          data-testid="calc-address"
          placeholder="Inizia a scrivere l'indirizzo... (es. Via Roma 42, Roma)"
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={() => (suggestions.length || error) && setOpen(true)}
          className="inp pr-9"
          autoComplete="off"
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2 text-[#6E7A71]">
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
        </div>
      </div>
      {open && (suggestions.length > 0 || error) && (
        <div
          data-testid="address-suggestions"
          className="absolute z-50 mt-1 w-full bg-white border border-[#DDE0D8] rounded-lg shadow-lg overflow-hidden max-h-72 overflow-y-auto"
        >
          {fallbackHint && (
            <div className="px-3 py-2 text-xs text-[#6E7A71] bg-[#EAB308]/10 border-b border-[#DDE0D8]">
              <AlertTriangle size={11} className="inline mr-1 text-[#EAB308]" /> {fallbackHint}
            </div>
          )}
          {suggestions.map((s, i) => (
            <button
              type="button"
              key={s.place_id || i}
              data-testid={`address-suggestion-${i}`}
              onClick={() => pick(s)}
              className="w-full text-left px-3 py-2.5 hover:bg-[#EAE7E0] border-b border-[#DDE0D8] last:border-b-0 flex items-start gap-2"
            >
              <MapPin size={12} className="mt-1 text-[#EAB308] flex-shrink-0" />
              <span className="text-sm text-[#17301C] leading-tight">{s.display_name}</span>
            </button>
          ))}
          {suggestions.length === 0 && error && (
            <div data-testid="address-error" className="px-3 py-3 text-sm text-[#6E7A71] flex items-center gap-2">
              <AlertTriangle size={13} className="text-[#EAB308]" /> {error}
            </div>
          )}
          <div className="px-3 py-1.5 text-[10px] uppercase tracking-widest text-[#6E7A71] bg-[#F9F8F6]">
            © OpenStreetMap contributors
          </div>
        </div>
      )}
    </div>
  );
}

// ============ SITE VISIT DIALOG ============
function SiteVisitDialog({ open, setOpen, form, result, user }) {
  const [slots, setSlots] = React.useState([]);
  const [selectedSlot, setSelectedSlot] = React.useState("");
  const [loadingSlots, setLoadingSlots] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [visit, setVisit] = React.useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: "",
    address: form?.address || "",
    notes: "",
  });

  React.useEffect(() => {
    if (!open) return;
    setVisit((v) => ({
      ...v,
      name: user?.name || v.name,
      email: user?.email || v.email,
      address: form?.address || v.address,
    }));
    setLoadingSlots(true);
    api.get("/site-visits/slots")
      .then(({ data }) => setSlots(data?.slots || []))
      .catch(() => toast.error("Errore caricamento slot"))
      .finally(() => setLoadingSlots(false));
  }, [open, user, form?.address]);

  const submitVisit = async (e) => {
    e.preventDefault();
    if (!selectedSlot) { toast.error("Scegli un orario"); return; }
    if (!visit.name || !visit.email || !visit.phone) { toast.error("Compila nome, email e telefono"); return; }
    setSubmitting(true);
    try {
      await api.post("/site-visits", {
        name: visit.name,
        email: visit.email,
        phone: visit.phone,
        slot: selectedSlot,
        address: visit.address,
        plant_type: form?.system_type || "residential",
        notes: visit.notes,
        calc_snapshot: result ? { ...form, result } : null,
      });
      toast.success("Prenotazione inviata! Ti richiameremo per confermare.");
      setOpen(false);
      setSelectedSlot("");
      setVisit({ ...visit, phone: "", notes: "" });
    } catch (err) {
      if (err?.response?.status === 409) toast.error("Slot appena occupato. Scegline un altro.");
      else toast.error("Errore invio prenotazione");
    } finally { setSubmitting(false); }
  };

  if (!open) return null;

  const fmtSlot = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString("it-IT", { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={() => setOpen(false)}
      data-testid="visit-dialog"
    >
      <div
        className="bg-[#F9F8F6] rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-[#17301C] text-white px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-2">
            <CalendarCheck size={20} className="text-[#EAB308]" />
            <h3 className="font-black text-lg">Prenota sopralluogo gratuito</h3>
          </div>
          <button data-testid="visit-close" onClick={() => setOpen(false)} className="p-1 hover:bg-white/10 rounded-full">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={submitVisit} className="p-6 space-y-5">
          <div className="text-sm text-[#3A4A3E] bg-[#EAB308]/10 p-3 rounded-lg border border-[#EAB308]/40">
            Un nostro tecnico ti richiamerà entro poche ore per confermare l&apos;orario e verificare i dettagli tecnici del sopralluogo.
          </div>

          <div>
            <div className="text-[10px] font-black tracking-widest uppercase text-[#6E7A71] mb-2">1. Scegli data e ora</div>
            {loadingSlots ? (
              <div className="flex items-center gap-2 text-sm text-[#6E7A71]"><Loader2 size={14} className="animate-spin" /> Carico slot disponibili...</div>
            ) : slots.length === 0 ? (
              <div className="text-sm text-[#6E7A71]">Nessuno slot disponibile al momento.</div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-52 overflow-y-auto p-1" data-testid="visit-slots">
                {slots.map((s) => (
                  <button
                    type="button"
                    key={s}
                    data-testid={`visit-slot-${s}`}
                    onClick={() => setSelectedSlot(s)}
                    className={`px-3 py-2 rounded-lg border text-xs font-semibold transition-colors ${selectedSlot === s ? "bg-[#17301C] text-white border-[#17301C]" : "bg-white border-[#DDE0D8] text-[#17301C] hover:bg-[#EAE7E0]"}`}
                  >
                    {fmtSlot(s)}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="text-[10px] font-black tracking-widest uppercase text-[#6E7A71] mb-2">2. I tuoi dati</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input data-testid="visit-name" placeholder="Nome e cognome *" value={visit.name} onChange={(e) => setVisit({ ...visit, name: e.target.value })} className="inp" required />
              <input data-testid="visit-email" type="email" placeholder="Email *" value={visit.email} onChange={(e) => setVisit({ ...visit, email: e.target.value })} className="inp" required />
              <input data-testid="visit-phone" type="tel" placeholder="Telefono *" value={visit.phone} onChange={(e) => setVisit({ ...visit, phone: e.target.value })} className="inp" required />
              <input data-testid="visit-address" placeholder="Indirizzo del sopralluogo" value={visit.address} onChange={(e) => setVisit({ ...visit, address: e.target.value })} className="inp" />
            </div>
            <textarea data-testid="visit-notes" placeholder="Note (es. orari preferiti, accesso al tetto, ecc.)" value={visit.notes} onChange={(e) => setVisit({ ...visit, notes: e.target.value })} className="inp mt-3" rows={3} />
          </div>

          <div className="flex items-center gap-3 justify-end pt-2">
            <button type="button" data-testid="visit-cancel" onClick={() => setOpen(false)} className="px-4 py-2 rounded-full border border-[#DDE0D8] text-[#17301C] font-semibold text-sm hover:bg-[#EAE7E0]">
              Annulla
            </button>
            <button
              type="submit"
              disabled={submitting || !selectedSlot}
              data-testid="visit-submit"
              className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#EAB308] text-[#17301C] font-black text-sm hover:bg-[#f5bc1b] transition-colors disabled:opacity-50"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <CalendarCheck size={14} />}
              {submitting ? "Invio..." : "Conferma prenotazione"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
